import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import apiClient from '../../services/apiClient';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';

const getCurrentUser = () => {
  const rawUser = sessionStorage.getItem('user') || localStorage.getItem('user');
  if (!rawUser) return {};

  try {
    return JSON.parse(rawUser);
  } catch {
    return {};
  }
};

const defaultRequestDetails = {
  requestType: 'New_vod',
  recordType: 'Account_vod',
  requestId: '',
  accountId: '',
  accountName: '',
  requestName: '',
};

const defaultRequestorDetails = {
  name: '',
  email: '',
  empNumber: '',
  profName: '',
};

const defaultHcoDetails = {
  name: '',
  dbaName: '',
  facilityType: '',
  specialtyType: '',
  status: '',
  ownerStatus: '',
  onekeyId: '',
  npiId: '',
  deaId: '',
  dddId: '',
  mdmId: '',
  primaryAddress: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateCode: '',
    zip5: '',
    zip4: '',
  },
};

const HCO_FIELD_MAP = {
  name: ['name'],
  dbaName: ['dbaName'],
  facilityType: ['facilityType'],
  specialtyType: ['specialtyType'],
  status: ['status'],
  ownerStatus: ['ownerStatus'],
  onekeyId: ['onekeyId'],
  npiId: ['npiId'],
  deaId: ['deaId'],
  dddId: ['dddId'],
  addrLn1: ['primaryAddress', 'addressLine1'],
  addrLn2: ['primaryAddress', 'addressLine2'],
  cityNm: ['primaryAddress', 'city'],
  stCd: ['primaryAddress', 'stateCode'],
  zipCd: ['primaryAddress', 'zip5'],
  zip4: ['primaryAddress', 'zip4'],
};

const setNestedValue = (source, path, value) => {
  const clone = structuredClone(source);
  let current = clone;
  for (let i = 0; i < path.length - 1; i += 1) {
    current = current[path[i]];
  }
  current[path[path.length - 1]] = value;
  return clone;
};

const normalizeRequestLine = (requestLine = {}) => {
  if (Array.isArray(requestLine)) return requestLine;
  return Object.keys(requestLine).map((key) => ({
    id: key,
    ...requestLine[key],
  }));
};

const applyEditedFields = (details, lines) => {
  let nextDetails = structuredClone(details);
  lines.forEach((line) => {
    const path = HCO_FIELD_MAP[line.fieldName];
    if (!path) return;
    nextDetails = setNestedValue(nextDetails, path, line.newValue ?? '');
  });
  return nextDetails;
};

const mapHcoResponse = (response) => {
  const account = response?.accountDetails || {};
  const lines = normalizeRequestLine(response?.requestLine);
  const details = {
    name: account.name || '',
    dbaName: account.dbaName || '',
    facilityType: account.facilityType || '',
    specialtyType: account.specialty || '',
    status: account.status || '',
    ownerStatus: account.ownerStatus || '',
    onekeyId: account.onekeyId || '',
    npiId: account.npiId || '',
    deaId: account.deaId || '',
    dddId: account.dddId || '',
    mdmId: response?.mdmId || '',
    primaryAddress: {
      addressLine1: account.addressLine || '',
      addressLine2: account.addressLine2 || '',
      city: account.city || '',
      stateCode: account.state || '',
      zip5: account.zip || '',
      zip4: account.zip4 || '',
    },
  };

  return {
    requestDetails: {
      requestType: response?.requestType || 'Edit_vod',
      recordType: response?.recordType || 'Account_vod',
      requestId: response?.requestId || '',
      accountId: response?.accountId || response?.mdmId || '',
      accountName: response?.accountName || account.name || '',
      requestName: response?.requestName || '',
    },
    requestorDetails: {
      name: response?.ownerDetails?.name || '',
      email: response?.ownerDetails?.email || '',
      empNumber: response?.ownerDetails?.empNumber || '',
      profName: response?.ownerDetails?.profileName || '',
    },
    hcoDetails: applyEditedFields(details, lines),
    baseHcoDetails: details,
    editedFields: lines.map((line) => ({
      ...line,
      mdmStatus: line.mdmStatus || 'Accepted',
      resolutionNote: line.resolutionNote || '',
    })),
  };
};

const buildSavePayload = (requestDetails, requestorDetails, hcoDetails, baseHcoDetails, editedFields) => {
  const finalFields = [...editedFields];
  
  // Top level fields
  const topLevelFields = ['name', 'dbaName', 'facilityType', 'specialtyType', 'status', 'ownerStatus', 'onekeyId', 'npiId', 'deaId', 'dddId'];
  topLevelFields.forEach(field => {
    if (hcoDetails[field] !== baseHcoDetails[field]) {
      const idx = finalFields.findIndex(f => f.fieldName === field);
      if (idx !== -1) {
        finalFields[idx] = { ...finalFields[idx], newValue: hcoDetails[field] ?? '', mdmStatus: finalFields[idx].mdmStatus || 'Accepted' };
      } else {
        finalFields.push({
          fieldName: field,
          oldValue: baseHcoDetails[field] || '',
          newValue: hcoDetails[field] ?? '',
          mdmStatus: 'Accepted',
          resolutionNote: '',
        });
      }
    }
  });

  // Address fields
  const addrMap = {
    addressLine1: 'addrLn1',
    addressLine2: 'addrLn2',
    city: 'cityNm',
    stateCode: 'stCd',
    zip5: 'zipCd',
    zip4: 'zip4'
  };

  Object.entries(addrMap).forEach(([field, fieldName]) => {
    if (hcoDetails.primaryAddress[field] !== baseHcoDetails.primaryAddress[field]) {
      const idx = finalFields.findIndex(f => f.fieldName === fieldName);
      if (idx !== -1) {
        finalFields[idx] = { ...finalFields[idx], newValue: hcoDetails.primaryAddress[field] ?? '', mdmStatus: finalFields[idx].mdmStatus || 'Accepted' };
      } else {
        finalFields.push({
          fieldName: fieldName,
          oldValue: baseHcoDetails.primaryAddress[field] || '',
          newValue: hcoDetails.primaryAddress[field] ?? '',
          mdmStatus: 'Accepted',
          resolutionNote: '',
        });
      }
    }
  });

  return {
    requestType: requestDetails.requestType,
    recordType: requestDetails.recordType,
    accountId: requestDetails.accountId,
    accountName: requestDetails.accountName,
    mdmAccountType: 'HCO',
    requestLine: finalFields.map((line) => ({
      fieldName: line.fieldName,
      oldValue: line.oldValue,
      newValue: line.newValue,
      resolutionNote: line.resolutionNote || '',
      mdmStatus: line.mdmStatus || 'Accepted',
    })),
    ownerDetails: {
      name: requestorDetails.name,
      email: requestorDetails.email,
      profileName: requestorDetails.profName,
      empNumber: requestorDetails.empNumber,
    },
    accountDetails: {
      name: hcoDetails.name,
      dbaName: hcoDetails.dbaName,
      addressLine: hcoDetails.primaryAddress.addressLine1,
      addressLine2: hcoDetails.primaryAddress.addressLine2,
      city: hcoDetails.primaryAddress.city,
      state: hcoDetails.primaryAddress.stateCode,
      zip: hcoDetails.primaryAddress.zip5,
      zip4: hcoDetails.primaryAddress.zip4,
      facilityType: hcoDetails.facilityType,
      ownerStatus: hcoDetails.ownerStatus,
      specialty: hcoDetails.specialtyType,
      npiId: hcoDetails.npiId,
      deaId: hcoDetails.deaId,
      onekeyId: hcoDetails.onekeyId,
      dddId: hcoDetails.dddId,
      status: hcoDetails.status,
    },
  };
};

const HCODCR = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error, execute } = useAPICall();
  const reqId = searchParams.get('reqId');
  const mdmId = searchParams.get('mdmId');
  const currentUser = useMemo(() => getCurrentUser(), []);

  const [requestDetails, setRequestDetails] = useState(defaultRequestDetails);
  const [requestorDetails, setRequestorDetails] = useState(defaultRequestorDetails);
  const [hcoDetails, setHcoDetails] = useState(defaultHcoDetails);
  const [baseHcoDetails, setBaseHcoDetails] = useState(defaultHcoDetails);
  const [editedFields, setEditedFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const response = await apiClient.get(API_CONFIG.auth.getUserInfo.endpoint);
        const user = response?.data || {};
        setRequestorDetails((prev) => ({
          ...prev,
          name: prev.name || user.name || '',
          email: prev.email || user.email || user.mail || '',
          empNumber: prev.empNumber || user.empNumber || '',
          profName: prev.profName || user.profileName || '',
        }));
      } catch {
      }
    };

    loadUserInfo();
  }, []);

  useEffect(() => {
    const init = async () => {
      if (reqId) {
        try {
          const response = await execute(API_CONFIG.workflow.getOpenDcrByRequestId, null, {
            params: { reqId },
            showErrorToast: false,
            errorMessage: 'Unable to load HCO DCR.',
          });
          const mapped = mapHcoResponse(response);
          setRequestDetails(mapped.requestDetails);
          setRequestorDetails((prev) => ({ ...prev, ...mapped.requestorDetails }));
          setHcoDetails(mapped.hcoDetails);
          setBaseHcoDetails(mapped.baseHcoDetails);
          setEditedFields(mapped.editedFields);
        } catch {
        }
        return;
      }

      if (mdmId) {
        try {
          const response = await execute(API_CONFIG.workflow.getHcoByMdmId, null, {
            params: { mdmId },
            showErrorToast: false,
            errorMessage: 'Unable to load HCO details.',
          });
          const account = response?.data || response || {};
          setRequestDetails((prev) => ({
            ...prev,
            requestType: 'Edit_vod',
            recordType: 'Account_vod',
            accountId: account.mdmId || mdmId,
            accountName: account.name || '',
          }));
          const mappedHco = {
            name: account.name || '',
            dbaName: account.dbaName || '',
            facilityType: account.facilityType || '',
            specialtyType: account.specialtyType || '',
            status: account.status || '',
            ownerStatus: account.ownerStatus || '',
            onekeyId: account.okWorkplaceId || account.onekeyId || '',
            npiId: account.npiId || '',
            deaId: account.deaId || '',
            dddId: account.dddId || '',
            mdmId: account.mdmId || mdmId,
            primaryAddress: {
              addressLine1: account.primaryAddress?.addressLine1 || '',
              addressLine2: account.primaryAddress?.addressLine2 || '',
              city: account.primaryAddress?.city || '',
              stateCode: account.primaryAddress?.stateCode || '',
              zip5: account.primaryAddress?.zip5 || '',
              zip4: account.primaryAddress?.zip4 || '',
            },
          };
          setHcoDetails(mappedHco);
          setBaseHcoDetails(mappedHco);
        } catch {
        }
      }
    };

    init();
  }, [execute, mdmId, reqId]);

  const updateField = (field, value) => {
    setHcoDetails((prev) => ({ ...prev, [field]: value }));
  };

  const updateAddressField = (field, value) => {
    setHcoDetails((prev) => ({
      ...prev,
      primaryAddress: {
        ...prev.primaryAddress,
        [field]: value,
      },
    }));
  };

  const updateEditedField = (index, key, value) => {
    setEditedFields((prev) => prev.map((line, lineIndex) => (
      lineIndex === index ? { ...line, [key]: value } : line
    )));
  };

  const saveDcr = async () => {
    const requestId = requestDetails.requestId || reqId;
    if (!requestId) {
      toast.error('Request ID is required.');
      return;
    }

    try {
      setSaving(true);
      await execute(API_CONFIG.inbox.createRequest, {
        data: buildSavePayload(requestDetails, requestorDetails, hcoDetails, baseHcoDetails, editedFields),
      }, {
        params: { requestId },
        headers: currentUser?.username ? { user: currentUser.username } : undefined,
        successMessage: 'HCO request saved successfully.',
        showSuccessToast: true,
        errorMessage: 'Unable to save HCO request.',
      });
    } catch {
      
    } finally {
      setSaving(false);
    }
  };

  const closeDcr = async () => {
    const requestId = requestDetails.requestId || reqId;
    if (!requestId) {
      toast.error('Request ID is required.');
      return;
    }

    try {
      setClosing(true);
      await execute(API_CONFIG.inbox.approveRequest, null, {
        params: { requestId },
        successMessage: 'Request closed successfully.',
        showSuccessToast: true,
        errorMessage: 'Unable to close request.',
      });
      navigate('/inbox/user');
    } catch {
    } finally {
      setClosing(false);
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      <Box sx={{ mb: 1.5 }}>
        <PageBreadcrumbs items={[
          { label: 'HCO Search', path: '/search/hco' },
          { label: 'HCO Details', path: mdmId ? `/details/hco/${mdmId}` : null },
          { label: reqId ? 'Edit HCO Request' : 'New HCO Request' }
        ].filter(item => item.label !== 'HCO Details' || item.path)} />
      </Box>
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: '12px', 
          overflow: 'hidden', 
          border: '1px solid #f1f5f9',
          backgroundColor: '#fff',
          mb: 1.5
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid #f1f5f9',
            backgroundColor: alpha('#1E88E5', 0.02),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '4px',
              height: '100%',
              backgroundColor: '#1E88E5'
            }
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.01em', mb: 0.5 }}>
              {reqId ? 'Edit HCO Request' : 'New HCO Request'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
              Managing change request for HCO ID: <span style={{ color: '#1E88E5', fontWeight: 700 }}>{mdmId || hcoDetails.mdmId || '-'}</span>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button 
              variant="contained" 
              disableElevation
              onClick={saveDcr} 
              disabled={saving || closing}
              sx={{ 
                borderRadius: '8px', 
                textTransform: 'none', 
                fontWeight: 700, 
                px: 3,
                backgroundColor: '#1E88E5',
                '&:hover': { backgroundColor: '#1565C0' }
              }}
            >
              {saving ? 'Saving...' : 'Save Request'}
            </Button>
            <Button 
              variant="contained" 
              disableElevation
              onClick={closeDcr} 
              disabled={saving || closing || !reqId}
              sx={{ 
                borderRadius: '8px', 
                textTransform: 'none', 
                fontWeight: 700, 
                px: 3,
                backgroundColor: '#334155',
                '&:hover': { backgroundColor: '#1e293b' }
              }}
            >
              {closing ? 'Closing...' : 'Finalize Request'}
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate(-1)} 
              disabled={saving || closing}
              sx={{ 
                borderRadius: '8px', 
                textTransform: 'none', 
                fontWeight: 700, 
                px: 3,
                borderColor: '#e2e8f0',
                color: '#64748b',
                backgroundColor: '#fff',
                '&:hover': { backgroundColor: '#f8fafc', borderColor: '#cbd5e1' }
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #f1f5f9', p: 2, mb: 1.5 }}>
          <Grid container spacing={2}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid item xs={12} md={3} key={i}>
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="90%" height={24} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #f1f5f9', borderRadius: '12px' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, color: '#1e293b' }}>Request Details</Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <TextField select fullWidth label="Request Type" value={requestDetails.requestType} disabled>
                    <MenuItem value={requestDetails.requestType}>{requestDetails.requestType}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField select fullWidth label="Record Type" value={requestDetails.recordType} disabled>
                    <MenuItem value={requestDetails.recordType}>{requestDetails.recordType}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth label="Request Id" value={requestDetails.requestId || reqId || ''} disabled />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth label="MDM Id" value={requestDetails.accountId || hcoDetails.mdmId} disabled />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Account Name"
                    value={requestDetails.accountName || hcoDetails.name}
                    onChange={(event) => setRequestDetails((prev) => ({ ...prev, accountName: event.target.value }))}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mt: 1, mb: 1.5, borderTop: '1px solid #f1f5f9', pt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Requestor Details</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth label="Name" value={requestorDetails.name} disabled />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={requestorDetails.email}
                    onChange={(event) => setRequestorDetails((prev) => ({ ...prev, email: event.target.value }))}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #f1f5f9', borderRadius: '12px' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, color: '#1e293b' }}>Edited Fields</Typography>
              <GlobalMaterialTable
                columns={[
                  { header: 'Field Name', id: 'fieldName', accessorFn: (row) => row.fieldName },
                  { header: 'Old Value', id: 'oldValue', accessorFn: (row) => row.oldValue || '-' },
                  { header: 'New Value', id: 'newValue', accessorFn: (row) => row.newValue || '-' },
                  {
                    header: 'Comment',
                    id: 'comment',
                    Cell: ({ row }) => (
                      <TextField
                        size="small"
                        fullWidth
                        value={row.original.resolutionNote || ''}
                        onChange={(event) => updateEditedField(row.index, 'resolutionNote', event.target.value)}
                      />
                    ),
                  },
                  { header: 'Status', id: 'status', accessorFn: (row) => row.mdmStatus || 'Accepted' },
                  {
                    header: 'Action',
                    id: 'action',
                    enableSorting: false,
                    Cell: ({ row }) => (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Box
                          onClick={() => currentUser?.name === 'Admin User' && updateEditedField(row.index, 'mdmStatus', 'Accepted')}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.8,
                            py: 0.8,
                            px: 1.5,
                            borderRadius: '8px',
                            cursor: currentUser?.name === 'Admin User' ? 'pointer' : 'default',
                            backgroundColor: row.original.mdmStatus === 'Accepted' ? '#10b981' : alpha('#10b981', 0.05),
                            color: row.original.mdmStatus === 'Accepted' ? '#fff' : '#10b981',
                            border: '1px solid',
                            borderColor: row.original.mdmStatus === 'Accepted' ? '#10b981' : alpha('#10b981', 0.2),
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: row.original.mdmStatus === 'Accepted' ? '#059669' : alpha('#10b981', 0.15),
                            },
                            opacity: currentUser?.name === 'Admin User' ? 1 : 0.6
                          }}
                        >
                          <CheckIcon sx={{ fontSize: '1rem' }} />
                          <Typography sx={{  fontWeight: 800 }}>Accept</Typography>
                        </Box>

                        <Box
                          onClick={() => currentUser?.name === 'Admin User' && updateEditedField(row.index, 'mdmStatus', 'Rejected')}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.8,
                            py: 0.8,
                            px: 1.5,
                            borderRadius: '8px',
                            cursor: currentUser?.name === 'Admin User' ? 'pointer' : 'default',
                            backgroundColor: row.original.mdmStatus === 'Rejected' ? '#ef4444' : alpha('#ef4444', 0.05),
                            color: row.original.mdmStatus === 'Rejected' ? '#fff' : '#ef4444',
                            border: '1px solid',
                            borderColor: row.original.mdmStatus === 'Rejected' ? '#ef4444' : alpha('#ef4444', 0.2),
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: row.original.mdmStatus === 'Rejected' ? '#dc2626' : alpha('#ef4444', 0.15),
                            },
                            opacity: currentUser?.name === 'Admin User' ? 1 : 0.6
                          }}
                        >
                          <CloseIcon sx={{ fontSize: '1rem' }} />
                          <Typography sx={{  fontWeight: 800 }}>Reject</Typography>
                        </Box>
                      </Box>
                    ),
                  },
                ]}
                data={editedFields}
                emptyMessage="No edited fields"
                enablePagination={false}
                enableBottomToolbar={false}
                getRowId={(row, index) => row.id || `${row.fieldName}-${index}`}
                muiTablePaperProps={{ elevation: 0, sx: { borderRadius: 2 } }}
              />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #f1f5f9', borderRadius: '12px' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, color: '#1e293b' }}>HCO Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}><TextField fullWidth label="Business Name" value={hcoDetails.name} onChange={(event) => updateField('name', event.target.value)} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="DBA Name" value={hcoDetails.dbaName} onChange={(event) => updateField('dbaName', event.target.value)} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Facility Type" value={hcoDetails.facilityType} onChange={(event) => updateField('facilityType', event.target.value)} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Specialty" value={hcoDetails.specialtyType} onChange={(event) => updateField('specialtyType', event.target.value)} /></Grid>
                <Grid item xs={12} md={2}>
                  <TextField select fullWidth label="Status" value={hcoDetails.status} onChange={(event) => updateField('status', event.target.value)}>
                    {['Active', 'Inactive'].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Owner Status" value={hcoDetails.ownerStatus} onChange={(event) => updateField('ownerStatus', event.target.value)} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="OneKey ID" value={hcoDetails.onekeyId} onChange={(event) => updateField('onekeyId', event.target.value)} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="NPI" value={hcoDetails.npiId} onChange={(event) => updateField('npiId', event.target.value)} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="DEA" value={hcoDetails.deaId} onChange={(event) => updateField('deaId', event.target.value)} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="DDD" value={hcoDetails.dddId} onChange={(event) => updateField('dddId', event.target.value)} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Address Line 1" value={hcoDetails.primaryAddress.addressLine1} onChange={(event) => updateAddressField('addressLine1', event.target.value)} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Address Line 2" value={hcoDetails.primaryAddress.addressLine2} onChange={(event) => updateAddressField('addressLine2', event.target.value)} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="City" value={hcoDetails.primaryAddress.city} onChange={(event) => updateAddressField('city', event.target.value)} /></Grid>
                <Grid item xs={12} md={1}><TextField fullWidth label="State" value={hcoDetails.primaryAddress.stateCode} onChange={(event) => updateAddressField('stateCode', event.target.value)} /></Grid>
                <Grid item xs={12} md={1}><TextField fullWidth label="Zip5" value={hcoDetails.primaryAddress.zip5} onChange={(event) => updateAddressField('zip5', event.target.value)} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Zip4" value={hcoDetails.primaryAddress.zip4} onChange={(event) => updateAddressField('zip4', event.target.value)} /></Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default HCODCR;
